/**
 * Portaled overlays (modals, full-screen pickers) live under document.body but still
 * bubble events through the React tree to ancestors like the flowchart container.
 * Document-level listeners (pan, selection box, drag) must ignore pointer events
 * when the target is inside these overlays.
 *
 * Add data-sprint-overlay-root on new portaled overlays for a single stable hook.
 */
const PORTAL_OVERLAY_SELECTOR = [
  '[data-sprint-overlay-root]',
  '.dep-warning-modal__backdrop',
  '.del-warning-modal__backdrop',
  '.nested-warning-modal__backdrop',
  '.nest-modal__overlay',
  '.confirm-modal__backdrop',
  '.campaign-picker__backdrop',
  '.decision-options-modal__backdrop',
  '.mission-full-view__backdrop',
  '.attachments-modal__backdrop',
  '.conditions-modal__backdrop',
  '.deliverables-modal__backdrop',
  '.deliverable-submit-modal__backdrop',
].join(', ');

const TEXT_NODE = typeof Node !== 'undefined' ? Node.TEXT_NODE : 3;

/**
 * Mouse events while selecting text often use a Text node as `target`, which has no `.closest`.
 * @param {EventTarget|null} target
 * @returns {Element|null}
 */
function normalizeToElement(target) {
  if (!target) return null;
  if (target.nodeType === TEXT_NODE) return target.parentElement;
  if (target.nodeType === 1 && typeof target.closest === 'function') return target;
  return null;
}

/**
 * @param {EventTarget|null} target
 * @returns {boolean}
 */
export function isPointerOverPortaledOverlay(target) {
  const el = normalizeToElement(target);
  if (!el) return false;
  return Boolean(el.closest(PORTAL_OVERLAY_SELECTOR));
}

/**
 * When hit-testing falls through (pointer-events: none, z-index/stacking), `elementFromPoint`
 * can return the flowchart even though a full-screen modal backdrop still covers that screen
 * position. This uses overlay geometry so we still block canvas interaction.
 *
 * @param {number} clientX
 * @param {number} clientY
 * @returns {boolean}
 */
export function isPointWithinBoundsOfVisibleBlockingOverlay(clientX, clientY) {
  if (typeof document === 'undefined' || typeof document.querySelectorAll !== 'function') {
    return false;
  }
  if (typeof clientX !== 'number' || typeof clientY !== 'number' || Number.isNaN(clientX) || Number.isNaN(clientY)) {
    return false;
  }
  let els;
  try {
    els = document.querySelectorAll(PORTAL_OVERLAY_SELECTOR);
  } catch {
    return false;
  }
  for (let i = 0; i < els.length; i++) {
    const el = els[i];
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') continue;
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) continue;
    if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) {
      return true;
    }
  }
  return false;
}

/**
 * Prefer this for document/window mousemove: `e.target` can be a Text node during drag-select,
 * and in rare cases it may not match the topmost element under the pointer.
 *
 * @param {MouseEvent} e
 * @returns {boolean}
 */
export function isPointerOverPortaledOverlayFromEvent(e) {
  if (isPointerOverPortaledOverlay(e.target)) return true;
  if (typeof document === 'undefined' || typeof document.elementFromPoint !== 'function') {
    return isPointWithinBoundsOfVisibleBlockingOverlay(e.clientX, e.clientY);
  }
  const top = document.elementFromPoint(e.clientX, e.clientY);
  if (isPointerOverPortaledOverlay(top)) return true;
  return isPointWithinBoundsOfVisibleBlockingOverlay(e.clientX, e.clientY);
}

/**
 * When true, window-level clipboard shortcuts (copy missions as JSON, etc.) must not
 * run so the browser can copy highlighted text — e.g. in a portaled modal while focus
 * remains on the flowchart (tabIndex).
 *
 * @param {KeyboardEvent} e
 * @returns {boolean}
 */
export function shouldDeferToNativeClipboardShortcuts(e) {
  const t = e.target;
  const focusEl = normalizeToElement(t);
  if (focusEl && (focusEl.tagName === 'INPUT' || focusEl.tagName === 'TEXTAREA' || focusEl.isContentEditable)) {
    return true;
  }
  if (isPointerOverPortaledOverlay(t)) {
    return true;
  }
  if (typeof window === 'undefined' || typeof window.getSelection !== 'function') {
    return false;
  }
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return false;
  if (!sel.toString()) return false;
  const range = sel.getRangeAt(0);
  const common = range.commonAncestorContainer;
  const elementNode = typeof Node !== 'undefined' ? Node.ELEMENT_NODE : 1;
  const el = common.nodeType === elementNode ? common : common.parentElement;
  if (el && typeof el.closest === 'function' && isPointerOverPortaledOverlay(el)) {
    return true;
  }
  return false;
}
